import React from "react";
import {
  QuestionnaireResultInterpretation,
  AiLearningExample,
  AiFeedbackPayload,
} from "services/questionnaireService";
import Card from "components/card";
import SummarySection from "./SummarySection";
import GeneralInterpretation from "./GeneralInterpretation";
import ClinicalInterpretations from "./ClinicalInterpretations";
import AiInterpretation from "./AiInterpretation";

interface InterpretationContainerProps {
  interpretation: QuestionnaireResultInterpretation;
  detailId: number | string;
  questionnaireType?: string;
  aiLearningExample?: AiLearningExample;
  onSubmitFeedback?: (payload: AiFeedbackPayload) => Promise<void>;
}

const InterpretationContainer: React.FC<InterpretationContainerProps> = ({
  interpretation,
  detailId,
  questionnaireType,
  aiLearningExample,
  onSubmitFeedback,
}) => {
  // If there is no interpretation, don't render anything
  if (!interpretation) {
    return (
      <div className="p-4 text-center text-gray-500">
        No hay interpretación disponible para este cuestionario.
      </div>
    );
  }

  console.log(
    "InterpretationContainer - questionnaire type:",
    questionnaireType
  );
  console.log("InterpretationContainer - interpretation:", interpretation);

  // Special handling for JAS questionnaire
  const renderJasInterpretation = () => {
    if (
      !interpretation.clinical_interpretations ||
      interpretation.clinical_interpretations.length === 0
    ) {
      return null;
    }

    return (
      <div className="mb-8">
        <h3 className="mb-4 text-lg font-semibold text-navy-700">
          Interpretación del Patrón de Conducta Tipo A
        </h3>

        <div className="space-y-4">
          {interpretation.clinical_interpretations.map((item, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
            >
              <p className="text-navy-700">{item}</p>
            </div>
          ))}

          {/* Explicación del Patrón de Conducta Tipo A */}
          <div className="rounded-lg bg-blue-50 p-4">
            <h5 className="mb-2 font-medium text-navy-700">
              ¿Qué es el Patrón de Conducta Tipo A?
            </h5>
            <p className="text-sm text-gray-700">
              El Patrón de Conducta Tipo A es un conjunto de características
              conductuales que incluyen impaciencia, competitividad, hostilidad
              y una sensación constante de urgencia temporal. Este patrón ha
              sido asociado con un mayor riesgo de enfermedades
              cardiovasculares.
            </p>
          </div>

          {/* Recomendaciones si es nivel alto */}
          {interpretation.interpretations &&
            interpretation.interpretations.tipo_a &&
            interpretation.interpretations.tipo_a
              .toLowerCase()
              .includes("alto") && (
              <div className="rounded-lg bg-orange-50 p-4">
                <h5 className="mb-2 font-medium text-navy-700">
                  Recomendaciones Clínicas
                </h5>
                <p className="text-sm text-gray-700">
                  Dada la presencia de un patrón de conducta Tipo A elevado,
                  podría ser beneficioso considerar:
                </p>
                <ul className="mt-2 list-inside list-disc text-sm text-gray-700">
                  <li>Técnicas de manejo del estrés y la ansiedad</li>
                  <li>Entrenamiento en relajación y mindfulness</li>
                  <li>
                    Reestructuración cognitiva para abordar pensamientos
                    perfeccionistas
                  </li>
                  <li>
                    Establecimiento de objetivos realistas y manejo del tiempo
                  </li>
                  <li>
                    Evaluación médica preventiva para factores de riesgo
                    cardiovascular
                  </li>
                </ul>
              </div>
            )}
        </div>
      </div>
    );
  };

  // Special handling for Holmes-Rahe questionnaire
  const renderHolmesRaheInterpretation = () => {
    if (
      !interpretation.clinical_interpretations ||
      interpretation.clinical_interpretations.length === 0
    ) {
      return null;
    }

    // Access risk_level from the interpretations object which doesn't have TypeScript errors
    const riskLevel = interpretation.interpretations?.risk_level || "";
    const getRiskLevelColor = (level: string): string => {
      level = level.toLowerCase();
      if (level === "bajo") return "bg-green-50 text-green-800";
      if (level === "medio" || level === "moderado")
        return "bg-yellow-50 text-yellow-800";
      if (level === "alto") return "bg-red-50 text-red-800";
      return "bg-blue-50 text-blue-800";
    };

    return (
      <div className="mb-8">
        <h3 className="mb-4 text-lg font-semibold text-navy-700">
          Interpretación del Nivel de Estrés
        </h3>

        <div className={`rounded-lg ${getRiskLevelColor(riskLevel)} mb-4 p-4`}>
          <div className="mb-2 font-medium">Nivel de Riesgo: {riskLevel}</div>
          <p className="text-sm">
            {typeof interpretation.interpretation === "string"
              ? interpretation.interpretation
              : ""}
          </p>
        </div>

        <div className="space-y-4">
          {interpretation.clinical_interpretations.map((item, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
            >
              <p className="text-navy-700">{item}</p>
            </div>
          ))}

          {/* Explicación de la Escala Holmes-Rahe */}
          <div className="rounded-lg bg-blue-50 p-4">
            <h5 className="mb-2 font-medium text-navy-700">
              ¿Qué es la Escala de Holmes y Rahe?
            </h5>
            <p className="text-sm text-gray-700">
              La Escala de Reajuste Psicosocial de Holmes y Rahe evalúa el nivel
              de estrés basado en eventos vitales experimentados durante el
              último año. La acumulación de Unidades de Cambio Vital (UCV) puede
              ser predictiva del riesgo de desarrollar problemas de salud
              relacionados con el estrés.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Calculate if we have various interpretation parts
  const hasSummary = !!interpretation.summary;
  const hasGeneralInterpretation =
    interpretation.interpretation &&
    (typeof interpretation.interpretation === "string" ||
      interpretation.interpretation.length > 0);
  const hasClinicalInterpretations =
    interpretation.clinical_interpretations &&
    interpretation.clinical_interpretations.length > 0 &&
    questionnaireType?.toUpperCase() !== "JAS" && // Don't show regular clinical for JAS
    questionnaireType?.toUpperCase() !== "HOLMES_RAHE"; // Don't show regular clinical for Holmes-Rahe
  const hasAiInterpretation = !!interpretation.ai_enhanced_interpretation;

  return (
    <Card extra="w-full p-6">
      <h2 className="mb-6 border-b border-gray-200 pb-2 text-xl font-bold text-navy-700">
        Interpretación
      </h2>

      <div className="space-y-8">
        {/* Summary section */}
        {hasSummary && <SummarySection summary={interpretation.summary} />}

        {/* General Interpretation - only show for default questionnaires */}
        {hasGeneralInterpretation &&
          questionnaireType?.toUpperCase() !== "HOLMES_RAHE" && (
            <GeneralInterpretation
              interpretation={interpretation.interpretation}
            />
          )}

        {/* Clinical interpretations */}
        {hasClinicalInterpretations && (
          <ClinicalInterpretations
            interpretations={interpretation.clinical_interpretations}
          />
        )}

        {/* JAS-specific interpretation */}
        {questionnaireType?.toUpperCase() === "JAS" &&
          renderJasInterpretation()}

        {/* Holmes-Rahe specific interpretation */}
        {questionnaireType?.toUpperCase() === "HOLMES_RAHE" &&
          renderHolmesRaheInterpretation()}
        {/* AI Enhanced interpretation - only show if available and if questionnaire type supports it */}
        {hasAiInterpretation && onSubmitFeedback && (
          <AiInterpretation
            aiEnhancedInterpretation={interpretation.ai_enhanced_interpretation}
            aiLearningExample={aiLearningExample}
            detailId={detailId.toString()}
            onSubmitFeedback={onSubmitFeedback}
          />
        )}
      </div>
    </Card>
  );
};

export default InterpretationContainer;
